import type { DateTime } from "luxon";
import type { NurseInShift, Shift } from "./models";

/**
 * Mirrors `allowed_shift_overlap` in `backend/utils.py`: when a new scheduled shift overlaps an existing one
 * by this much or less, `insert_nurses_to_shift` trims one of them instead of refusing the insert with
 * `error.shift.already.exists`. Keep the two in lockstep — the dialog predicts the server's answer from it.
 */
export const ALLOWED_SHIFT_OVERLAP_HOURS = 1;

const MILLIS_PER_HOUR = 60 * 60 * 1000;

export type ShiftWindow = { startTime: DateTime; endTime: DateTime };

/**
 * What saving a new shift for this person, at these times and location, would do to their day. Values are
 * kebab-case because they also name the dialog's hint test id.
 */
export enum QuickAddConflictKind {
	/** Nothing else scheduled that day — a plain add. */
	None = "none",
	/** Other shifts that day, none overlapping beyond the allowance — the add goes through as a second shift. */
	Double = "double",
	/** Exactly one overlapping shift, somewhere else — saving moves that shift here. */
	Move = "move",
	/** Exactly one overlapping shift, already in this location — there is nothing to add or move. */
	SameLocation = "same-location",
	/** Two or more overlapping shifts — too ambiguous to pick one to move; the server would refuse an add. */
	MultipleOverlap = "multiple-overlap",
}

/** A shift the server will cut short, and the time it will then end at. */
export type TrimmedShift = { shift: NurseInShift; newEndTime: DateTime };

/** How the server settles the overlaps within the allowance that saving at these times leaves. */
export type SmallOverlapTrims = {
	/** EARLIER existing shifts the server cuts back to the saved shift's start. */
	trimmed: TrimmedShift[];
	/** The LATER existing shift that makes the server end the SAVED shift at its start; `null` when none does. */
	shortenedBy: TrimmedShift | null;
};

export type QuickAddConflict =
	| { kind: QuickAddConflictKind.None }
	| ({ kind: QuickAddConflictKind.Double; shifts: NurseInShift[] } & SmallOverlapTrims)
	// A move's update settles its other small overlaps the same way the insert does (`update_nurses_from_shift`).
	| ({ kind: QuickAddConflictKind.Move; existing: NurseInShift } & SmallOverlapTrims)
	| { kind: QuickAddConflictKind.SameLocation; existing: NurseInShift }
	| { kind: QuickAddConflictKind.MultipleOverlap; overlapping: NurseInShift[] };

/** Hours two windows share; 0 when they only touch or do not meet. */
export const overlapHours = (a: ShiftWindow, b: ShiftWindow): number => {
	const overlapMs = Math.min(a.endTime.toMillis(), b.endTime.toMillis()) - Math.max(a.startTime.toMillis(), b.startTime.toMillis());
	return Math.max(0, overlapMs) / MILLIS_PER_HOUR;
};

/** Same instants, regardless of the zone either side was expressed in. */
export const isSameShiftWindow = (a: ShiftWindow, b: ShiftWindow): boolean =>
	a.startTime.toMillis() === b.startTime.toMillis() && a.endTime.toMillis() === b.endTime.toMillis();

/**
 * Every person's scheduled shifts on one day of a week, earliest first, keyed by nurse id. A zero-length shift is
 * left out, as the server's overlap check skips it too. A pending shift (not yet published) is kept: the server
 * already holds it and will refuse an overlapping insert. A shift marked for deletion is left out because it is
 * leaving the schedule, so it is neither offered as a move nor counted as a clash. The server does NOT skip it —
 * its insert check filters only rows already deleted — so an add over one is refused (409) and the add's error
 * snackbar says so; the dialog simply doesn't predict that case.
 *
 * Each entry is a shallow snapshot, not the schedule's own object: saving a move rewrites the live shift in
 * place (`updateNurseLocationAndTimes`), and a caller still describing the move afterwards — "moved from X" —
 * must read where the shift WAS, not where it now is.
 *
 * Days are matched by DATE, not by position. The backend builds each shift type's row separately and leaves out a
 * day that row has no staffing entry for (`backend/services/department.py`), so two rows of one week need not
 * have the same days, or the same day at the same index. A row with no entry for `dayDate` has nobody scheduled
 * on it that day, which is exactly what leaving it out says.
 */
export const groupNurseShiftsForDay = (weeklyShifts: readonly Shift[], dayDate: DateTime): Map<number, NurseInShift[]> => {
	const byNurse = new Map<number, NurseInShift[]>();
	for (const shift of weeklyShifts) {
		const day = shift.days.find((candidate) => candidate.date.valueOf() === dayDate.valueOf());
		if (!day) continue;
		for (const nurseInShift of day.nurses) {
			if (nurseInShift.isPendingToDelete || nurseInShift.startTime.toMillis() === nurseInShift.endTime.toMillis()) continue;
			const snapshot: NurseInShift = { ...nurseInShift };
			const existing = byNurse.get(nurseInShift.nurseId);
			if (existing) existing.push(snapshot);
			else byNurse.set(nurseInShift.nurseId, [snapshot]);
		}
	}
	for (const shifts of byNurse.values()) shifts.sort((a, b) => a.startTime.toMillis() - b.startTime.toMillis());
	return byNurse;
};

/**
 * A location as the grid groups by it: missing, `null` (what the API sends for an unassigned shift, though the
 * type says `undefined`) and empty all mean "no location" — the Unassigned group.
 */
export const assignedLocation = (location: string | null | undefined): string | null => (location ? location : null);

/**
 * The two ways the server settles an overlap within the allowance — on insert (`insert_nurses_to_shift`) and on a
 * move's update alike (`update_nurses_from_shift`): an existing shift that starts first is cut back to the saved
 * start; one that starts later instead ends the SAVED shift at its own start — the earliest such shift, since the
 * saved shift cannot run past it.
 */
const smallOverlapTrims = (shifts: readonly NurseInShift[], window: ShiftWindow): SmallOverlapTrims => {
	const overlapping = shifts.filter((shift) => overlapHours(shift, window) > 0);
	const startsFirst = (shift: NurseInShift) => shift.startTime.toMillis() <= window.startTime.toMillis();
	const [firstLater] = overlapping.filter((shift) => !startsFirst(shift));
	return {
		trimmed: overlapping.filter(startsFirst).map((shift) => ({ shift, newEndTime: window.startTime })),
		shortenedBy: firstLater ? { shift: firstLater, newEndTime: firstLater.startTime } : null,
	};
};

const blockingShifts = (sameDayShifts: readonly NurseInShift[], window: ShiftWindow): NurseInShift[] =>
	sameDayShifts.filter((shift) => overlapHours(shift, window) > ALLOWED_SHIFT_OVERLAP_HOURS);

/**
 * Classify a quick add against the person's other shifts that day, using the server's own overlap rule so the
 * dialog can say what saving will do before it is clicked, instead of letting the insert fail.
 *
 * `location` is the destination picked in the dialog. Only a real destination equal to the shift's own location is
 * "already there": no destination (nothing picked) never matches — so a single overlap still reads as a move and
 * the dialog's required-location rule holds the save — and neither does an unassigned shift.
 */
export const resolveQuickAddConflict = ({
	sameDayShifts,
	window,
	location,
}: {
	sameDayShifts: readonly NurseInShift[];
	window: ShiftWindow;
	location: string | null;
}): QuickAddConflict => {
	if (sameDayShifts.length === 0) return { kind: QuickAddConflictKind.None };

	const blocking = blockingShifts(sameDayShifts, window);
	if (blocking.length > 1) return { kind: QuickAddConflictKind.MultipleOverlap, overlapping: blocking };

	const [existing] = blocking;
	if (!existing) return { kind: QuickAddConflictKind.Double, shifts: [...sameDayShifts], ...smallOverlapTrims(sameDayShifts, window) };

	const destination = assignedLocation(location);
	if (destination !== null && assignedLocation(existing.location) === destination) return { kind: QuickAddConflictKind.SameLocation, existing };
	const others = sameDayShifts.filter((shift) => shift.id !== existing.id);
	return { kind: QuickAddConflictKind.Move, existing, ...smallOverlapTrims(others, window) };
};

/** The one shift that overlaps `window` beyond the allowance — wherever it is — or `undefined` when none or several do. */
export const singleBlockingShift = (sameDayShifts: readonly NurseInShift[], window: ShiftWindow): NurseInShift | undefined => {
	const blocking = blockingShifts(sameDayShifts, window);
	return blocking.length === 1 ? blocking[0] : undefined;
};

/** The three windows a pick is judged by: on screen now, last set by the dialog itself, and the section's own. */
export type QuickAddPickWindows = { currentWindow: ShiftWindow; seededWindow: ShiftWindow; sectionWindow: ShiftWindow };

/**
 * What picking a person sets in the dialog: their job, and the times (`null` keeps the user's own). The
 * destination is not part of it — it follows the current clash live (`useQuickAddSelection.locationFor`).
 */
export type QuickAddPickSeed = { jobId: string | undefined; window: ShiftWindow | null };

/**
 * What picking a staff row should set. A row for someone already on the day stands for ONE of their shifts, and
 * picking it chooses that shift: the times come from it (the section's are only a guess, and applying them to a
 * move would silently lengthen or shorten it), and so does its job — a move keeps it, and the time picker offers
 * that job's lengths — even one the person no longer holds, which the job picker then shows as unselected. That
 * holds even when the shift doesn't overlap the section's times: the row is the user's choice of shift.
 *
 * A plain row (nobody on the day) starts the add on the person's first job, as it always has. Its times are the
 * section's, unless the user typed their own (the current window differs from the one the dialog last set) —
 * those survive a change of person, while times the dialog set for someone else are put back.
 */
export const resolvePickSeed = ({
	pickedShift,
	firstJobId,
	currentWindow,
	seededWindow,
	sectionWindow,
}: {
	pickedShift: NurseInShift | undefined;
	firstJobId: string | undefined;
} & QuickAddPickWindows): QuickAddPickSeed => {
	if (pickedShift) return { jobId: pickedShift.jobId, window: { startTime: pickedShift.startTime, endTime: pickedShift.endTime } };
	const userEditedTimes = !isSameShiftWindow(currentWindow, seededWindow);
	return { jobId: firstJobId, window: userEditedTimes ? null : sectionWindow };
};

/**
 * The job the dialog shows, offers shift lengths for, and saves — one answer for all three, so the picker, the
 * time options and the save can never disagree.
 *
 * On a move the shift keeps its own job unless the user picked one, so an untouched picker shows THE MOVING
 * SHIFT's job — whichever shift that is now, since editing the times can turn the save into a move (or into a
 * move of a different shift) after the pick. It may be a job the person no longer holds; the picker then shows
 * none selected. On an add, a job the person doesn't hold is not one the add may use.
 */
export const shownJobIdFor = ({
	jobId,
	jobTouched,
	heldJobIds,
	movingShift,
}: {
	jobId: string | undefined;
	jobTouched: boolean;
	heldJobIds: readonly string[];
	movingShift: NurseInShift | undefined;
}): string | undefined => {
	if (movingShift) return jobTouched ? jobId : movingShift.jobId;
	if (jobId !== undefined && heldJobIds.includes(jobId)) return jobId;
	// DEFAULT: an add whose job is not one the person holds (or a cleared picker) uses their first held job — the add
	// path's pre-existing default (prepareDataForAddNurse stamps jobs[0]), now also shown in the picker so nothing is hidden.
	const [firstJobId] = heldJobIds;
	return firstJobId;
};
